/* @jsx h */
import { useMenu } from "@app/vue/providers/index";
import { defineComponent, ref, h, watch, computed } from "vue";
import { useLocalization } from "@lemmonhq/uikit";

export const ScheduleSection = defineComponent({
  setup() {
    const { cart } = useMenu();
    const { locale, t } = useLocalization();
    const scheduleType = ref<"now" | "later">("now");
    const selectedDate = ref("");
    const selectedTime = ref("");

    // Initialize from cart if schedule exists
    if (cart.cart.schedule) {
      scheduleType.value = "later";
      selectedDate.value = cart.cart.schedule.date;
      selectedTime.value = cart.cart.schedule.time;
    }

    // Get today's date in YYYY-MM-DD format
    const today = computed(() => {
      const now = new Date();
      return now.toISOString().split("T")[0];
    });

    // Get current time in HH:MM format
    const currentTime = computed(() => {
      const now = new Date();
      return now.toTimeString().slice(0, 5);
    });

    // Watch for changes and update cart
    watch([scheduleType, selectedDate, selectedTime], ([type, date, time]) => {
      if (type === "later" && date && time) {
        cart.cart.schedule = { date, time };
      } else {
        cart.cart.schedule = undefined;
      }
    });

    const selectNow = () => {
      scheduleType.value = "now";
      selectedDate.value = "";
      selectedTime.value = "";
    };

    const selectLater = () => {
      scheduleType.value = "later";
      if (!selectedDate.value) {
        selectedDate.value = today.value;
      }
      if (!selectedTime.value) {
        // Set default time to 1 hour from now
        const now = new Date();
        now.setHours(now.getHours() + 1);
        selectedTime.value = now.toTimeString().slice(0, 5);
      }
    };

    return () => (
      <div class="p-2">
        <h3 class="text-lg font-semibold mb-4">
          {t("customer.checkout.schedule_order")}
        </h3>

        <div class="space-y-4">
          {/* Schedule Type Selection */}
          <div class="space-y-3">
            <label
              class={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                scheduleType.value === "now"
                  ? "border-primary bg-primary/5"
                  : ""
              }`}
            >
              <input
                type="radio"
                name="scheduleType"
                value="now"
                checked={scheduleType.value === "now"}
                onChange={selectNow}
                class="radio radio-primary"
              />
              <div class="flex-1">
                <div class="font-medium">{t("customer.checkout.now")}</div>
                <div class="text-sm text-gray-600">
                  {t("customer.checkout.prepare_asap")}
                </div>
              </div>
              <span class="material-symbols-outlined">schedule</span>
            </label>

            <label
              class={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                scheduleType.value === "later"
                  ? "border-primary bg-primary/5"
                  : "border-base-300"
              }`}
            >
              <input
                type="radio"
                name="scheduleType"
                value="later"
                disabled={true}
                checked={scheduleType.value === "later"}
                onChange={selectLater}
                class="radio radio-primary"
              />
              <div class="flex-1">
                <div class="font-medium">
                  {t("customer.checkout.schedule_later")}
                </div>
                <div class="text-sm text-gray-600">
                  {t("customer.checkout.choose_date_time")}
                </div>
              </div>
              <span class="material-symbols-outlined">event</span>
            </label>
          </div>

          {/* Date and Time Selection */}
          {scheduleType.value === "later" && (
            <div class="space-y-3 pl-4 border-l-2 border-primary/20">
              <div>
                <label class="label">
                  <span class="label-text font-medium">
                    {t("customer.checkout.date")}
                  </span>
                </label>
                <input
                  v-model={selectedDate.value}
                  type="date"
                  min={today.value}
                  class="input input-bordered w-full"
                  required
                />
              </div>

              <div>
                <label class="label">
                  <span class="label-text font-medium">
                    {t("customer.checkout.time")}
                  </span>
                </label>
                <input
                  v-model={selectedTime.value}
                  type="time"
                  min={
                    selectedDate.value === today.value
                      ? currentTime.value
                      : "00:00"
                  }
                  class="input input-bordered w-full"
                  required
                />
              </div>

              {selectedDate.value && selectedTime.value && (
                <div class="bg-base-200 rounded-lg p-3">
                  <div class="text-sm font-medium text-primary">
                    {t("customer.checkout.scheduled_for")}
                  </div>
                  <div class="text-sm">
                    {new Date(
                      `${selectedDate.value}T${selectedTime.value}`,
                    ).toLocaleDateString(locale.value || "en", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}{" "}
                    {t("customer.checkout.at_time", [selectedTime.value])}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
});
